// The locale set, and everything derived from it. This module hides ONE
// decision — which languages the site ships — behind a type and four lookups.
// Adding a locale is an edit here plus a content module; no component that
// merely renders copy has any reason to change, which is the property that
// keeps the locale set from leaking across the codebase.

export const LOCALES = ["en", "de"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * English is the primary language and keeps the bare path (`/`, `/platforms`);
 * German is additive under `/de/*`. That split is why no existing indexed URL
 * moves when a locale is added.
 *
 * There is deliberately no `Accept-Language` negotiation. The site is static and
 * CDN-cached, so negotiating at the edge either fragments the cache key or needs
 * edge compute, and it makes the canonical URL of `/` ambiguous to a crawler.
 * The language switcher is the mechanism, and it is one click.
 */
export const DEFAULT_LOCALE: Locale = "en";

/**
 * The endonym — the language's name in itself. This is the switcher control's
 * accessible name, so a screen reader announces "Deutsch", never "D-E".
 */
export const LOCALE_LABELS: Readonly<Record<Locale, string>> = {
  en: "English",
  de: "Deutsch",
};

/**
 * The two-letter code the switcher chip DISPLAYS. A separate map from
 * `LOCALE_LABELS` rather than a `toUpperCase()` on the locale id, because the
 * two are read by different audiences and only the endonym is the accessible
 * name. Deriving it would be right for exactly these two locales and wrong for
 * the first one where it is not — `pt-BR` displays "BR", not "PT-BR" — and the
 * point of a map is that such a locale is an edit here rather than a bug.
 */
export const LOCALE_CODES: Readonly<Record<Locale, string>> = {
  en: "EN",
  de: "DE",
};

/** The `lang` attribute the document carries for each locale. */
export const LOCALE_HTML_LANG: Readonly<Record<Locale, string>> = {
  en: "en",
  de: "de",
};

/**
 * The only narrowing path from an arbitrary string (a URL segment) to `Locale`.
 * Case-sensitive on purpose: URLs are lowercase here, and accepting `/DE/` would
 * mint a second URL for the same content that `hreflang` does not declare.
 */
export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}
